import { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import type { ReactNode } from 'react';
import type {
  SyncDocument as SyncDocumentType,
  SyncProvider as SyncProviderType,
  AwarenessManager as AwarenessManagerType,
  UndoManager as UndoManagerType,
  SyncState,
} from '@annoteer/sync-layer';
import { usePlatform } from './PlatformContext.js';
import { useAuth } from './AuthContext.js';

// Lazy-load sync-layer to defer Yjs from the initial bundle
/* eslint-disable-next-line @typescript-eslint/consistent-type-imports */
type SyncLayerModule = typeof import('@annoteer/sync-layer');
let syncLayerCache: SyncLayerModule | null = null;
let syncLayerPromise: Promise<SyncLayerModule> | null = null;

function getSyncLayer(): Promise<SyncLayerModule> {
  if (syncLayerCache) return Promise.resolve(syncLayerCache);
  if (!syncLayerPromise) {
    syncLayerPromise = import('@annoteer/sync-layer').then((mod) => {
      syncLayerCache = mod;
      return mod;
    });
  }
  return syncLayerPromise;
}

interface SyncContextValue {
  syncDoc: SyncDocumentType | null;
  syncProvider: SyncProviderType | null;
  awareness: AwarenessManagerType | null;
  undoManager: UndoManagerType | null;
  connectionState: SyncState;
  connectToDocument: (documentId: string) => void;
}

const SyncContext = createContext<SyncContextValue | null>(null);

const WS_URL =
  import.meta.env.VITE_WS_URL ||
  `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}/yjs`;

/**
 * Decode a JWT token's exp claim without verification.
 * Returns expiry time in milliseconds since epoch, or null if not a valid JWT.
 */
function getTokenExpiryMs(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1])) as { exp?: number };
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function SyncContextProvider({ children }: { children: ReactNode }) {
  const { context, user } = usePlatform();
  // Announced over awareness so the presenter's side panel can tell its own
  // stage apart from other participants, and relay the page it is showing.
  const surface: 'panel' | 'stage' = context?.frameContext === 'meetingStage' ? 'stage' : 'panel';
  const { getToken, user: authUser } = useAuth();

  // The validated SSO token's `name` claim is the authoritative display name —
  // it is what Entra ID actually holds for the account. The Teams context copy
  // is frequently an empty string, so prefer the token and keep the context
  // value only as a fallback. Cursor labels are built from this.
  const displayName = authUser?.name?.trim() || user?.displayName || 'Unknown User';
  const [syncDoc, setSyncDoc] = useState<SyncDocumentType | null>(null);
  const [connectionState, setConnectionState] = useState<SyncState>('disconnected');
  const providerRef = useRef<SyncProviderType | null>(null);
  const awarenessRef = useRef<AwarenessManagerType | null>(null);
  const undoRef = useRef<UndoManagerType | null>(null);
  const syncDocRef = useRef<SyncDocumentType | null>(null);
  const stateUnsubRef = useRef<(() => void) | null>(null);
  const wsRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stateUnsubRef.current?.();
      providerRef.current?.disconnect();
      if (wsRefreshTimerRef.current) clearTimeout(wsRefreshTimerRef.current);
    };
  }, []);

  const connectToDocument = useCallback(
    async (documentId: string) => {
      const { SyncDocument, SyncProvider, AwarenessManager, UndoManager } = await getSyncLayer();

      // Create instances lazily on first connect
      if (!providerRef.current) {
        providerRef.current = new SyncProvider();
        stateUnsubRef.current = providerRef.current.onStateChange(setConnectionState);
      }
      if (!awarenessRef.current) {
        awarenessRef.current = new AwarenessManager();
      }
      if (!undoRef.current) {
        undoRef.current = new UndoManager();
      }

      // Cleanup previous document
      syncDocRef.current?.destroy();
      undoRef.current.destroy();

      const meetingId = context?.meetingId ?? 'dev-meeting';
      const doc = new SyncDocument({ meetingId, documentId });

      // Connect WebSocket provider with auth token
      const authToken = getToken();
      providerRef.current.connect(doc, {
        serverUrl: WS_URL,
        roomName: doc.roomName,
        token: authToken ?? undefined,
      });

      // Schedule WebSocket token refresh before expiry (for meetings > 1hr)
      if (wsRefreshTimerRef.current) clearTimeout(wsRefreshTimerRef.current);
      if (authToken) {
        const expiryMs = getTokenExpiryMs(authToken);
        if (expiryMs) {
          // Refresh 5 minutes before expiry, minimum 30 seconds
          const refreshIn = Math.max(expiryMs - Date.now() - 5 * 60_000, 30_000);
          wsRefreshTimerRef.current = setTimeout(() => {
            if (providerRef.current && syncDocRef.current) {
              const freshToken = getToken();
              console.log('[sync] Refreshing WebSocket token');
              providerRef.current.reconnect(syncDocRef.current, {
                serverUrl: WS_URL,
                roomName: syncDocRef.current.roomName,
                token: freshToken ?? undefined,
              });

              // Re-initialize awareness on the new provider
              const newProvider = providerRef.current.provider;
              if (newProvider?.awareness && awarenessRef.current) {
                awarenessRef.current.initialize(newProvider.awareness);
                if (user) {
                  const color = generateUserColor(user.id);
                  awarenessRef.current.setLocalUser(displayName, color, surface, user.id);
                }
              }
            }
          }, refreshIn);
        }
      }

      // Initialize awareness after provider connects
      const provider = providerRef.current.provider;
      if (provider?.awareness) {
        awarenessRef.current.initialize(provider.awareness);
        // Exposed for e2e only, alongside __annoteerCanvas and __univerAPI.
        // `28-awareness-cursor-collision` asserts the annotation pointer really
        // is in the awareness state before switching documents -- without that
        // check the test would quietly stop reproducing its bug and still pass.
        (window as unknown as Record<string, unknown>).__annoteerAwareness = provider.awareness;
        if (user) {
          const color = generateUserColor(user.id);
          awarenessRef.current.setLocalUser(displayName, color, surface, user.id);
        }
      }

      // Initialize undo manager
      undoRef.current.initialize(doc.annotations);

      syncDocRef.current = doc;
      setSyncDoc(doc);
    },
    // `displayName` MUST be here: it derives from the auth token, which is null
    // until SSO completes, so a callback that captured the pre-auth value would
    // keep labelling cursors "Unknown User" forever. Same class of stale-closure
    // bug as the missing `getToken` that once broke the WebSocket upgrade.
    [context?.meetingId, user, getToken, displayName, surface],
  );

  return (
    <SyncContext.Provider
      value={{
        syncDoc,
        syncProvider: providerRef.current,
        awareness: awarenessRef.current,
        undoManager: undoRef.current,
        connectionState,
        connectToDocument,
      }}
    >
      {children}
    </SyncContext.Provider>
  );
}

export function useSync(): SyncContextValue {
  const ctx = useContext(SyncContext);
  if (!ctx) {
    throw new Error('useSync must be used within SyncContextProvider');
  }
  return ctx;
}

function generateUserColor(userId: string): string {
  const colors = [
    '#ef4444',
    '#f97316',
    '#eab308',
    '#22c55e',
    '#06b6d4',
    '#3b82f6',
    '#8b5cf6',
    '#ec4899',
  ];
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = ((hash << 5) - hash + userId.charCodeAt(i)) | 0;
  }
  return colors[Math.abs(hash) % colors.length];
}
